import { api } from "@/lib/api-client";

export type AnalyticsPeriod = "7d" | "30d" | "90d" | string;

export type CallStatsBlock = {
  total: number;
  inbound?: number;
  outbound?: number;
  completed: number;
  failed: number;
  success_rate: number;
  total_minutes: number;
  avg_duration_seconds: number;
  total_duration_seconds?: number;
  change_from_previous?: {
    total?: number | null;
    completed?: number | null;
  };
};

export type TopAgent = {
  agent_id: string;
  agent_name: string;
  calls: number;
  total_minutes: number;
};

export type AgentComparison = {
  agent_id: string;
  agent_name: string;
  total_calls: number;
  completed: number;
  failed: number;
  success_rate: number;
  total_minutes: number;
  avg_duration_seconds: number;
  sentiment_score: number | null;
};

export type AgentsBlock = {
  active?: number;
  top_performing?: TopAgent[];
  comparison?: AgentComparison[];
};

export type IntentRow = { intent: string; count: number };
export type FunnelRow = { stage: string; count: number; rate: number };

export type SentimentBlock = {
  positive?: number;
  neutral?: number;
  negative?: number;
};

export type CostsBlock = {
  total_cents?: number;
};

export type AnalyticsOverview = {
  period?: string;
  calls?: CallStatsBlock;
  agents?: AgentsBlock;
  sentiment?: SentimentBlock;
  top_intents?: IntentRow[];
  conversion_funnel?: FunnelRow[];
  costs?: CostsBlock & { currency?: string };
};

export type CallsTimePoint = {
  period: string;
  total_calls: number;
  completed?: number;
  failed?: number;
  total_minutes?: number;
  avg_duration_seconds?: number;
  sentiment_score?: number | null;
};

export type LatencyAnalytics = {
  period?: string;
  summary: {
    sample_count: number;
    avg_e2e_ms: number;
    p50_e2e_ms: number;
    p95_e2e_ms: number;
    avg_llm_ms: number;
    avg_tts_ms: number;
  };
  trend: Array<{ period: string; avg_e2e_ms: number; samples: number }>;
  distribution: Array<{ bucket: string; count: number }>;
};

export type PctileBlock = {
  avg: number;
  p50: number;
  p90: number;
  p95: number;
  p99: number;
  samples: number;
};

export type LatencyConsole = {
  period?: string;
  pipeline_version: string;
  summary: {
    sample_count: number;
    ttfa: PctileBlock;
    stages: {
      endpointing: PctileBlock;
      stt: PctileBlock;
      llm_ttft: PctileBlock;
      tts_ttfb: PctileBlock;
      tool: PctileBlock;
    };
  };
  waterfall: Array<{ stage: string; avg_ms: number }>;
  distribution: Array<{ bucket: string; count: number }>;
  providers: Array<{
    stt: string;
    llm: string;
    llm_model: string;
    tts: string;
    samples: number;
    avg_ttfa_ms: number;
    p50_ttfa_ms: number;
    p95_ttfa_ms: number;
  }>;
  worst_calls: Array<{
    call_id: string;
    agent_name: string;
    started_at: string | null;
    turn_index: number;
    ttfa_ms: number;
    endpointing_ms: number;
    stt_ms: number;
    llm_ttft_ms: number;
    tts_ttfb_ms: number;
  }>;
};

export type ChannelFilter = "whatsapp" | "phone";

export const analyticsApi = {
  overview: async (params: {
    period?: AnalyticsPeriod;
    agent_id?: string;
    channel?: ChannelFilter;
  }) => {
    const res = await api.get<AnalyticsOverview>("/analytics/overview/", {
      params,
    });
    return res.data;
  },
  calls: async (params: {
    period?: AnalyticsPeriod;
    group_by?: "day" | "hour" | "week";
    agent_id?: string;
    channel?: ChannelFilter;
  }) => {
    const res = await api.get<{ data: CallsTimePoint[] }>("/analytics/calls/", {
      params,
    });
    return res.data;
  },

  latency: async (params: {
    period?: AnalyticsPeriod;
    agent_id?: string;
    channel?: ChannelFilter;
  }) => {
    const res = await api.get<LatencyAnalytics>("/analytics/latency/", {
      params,
    });
    return res.data;
  },

  latencyConsole: async (params: {
    period?: AnalyticsPeriod;
    agent_id?: string;
    pipeline_version?: string;
    channel?: ChannelFilter;
  }) => {
    const res = await api.get<LatencyConsole>("/analytics/latency_console/", {
      params,
    });
    return res.data;
  },

  exportReport: async (data: {
    format?: "csv" | "pdf";
    report_type?: "calls" | string;
    period?: Record<string, unknown>;
    filters?: Record<string, unknown>;
  }) => {
    const res = await api.post("/analytics/export/", data, {
      responseType: "blob",
    });
    return res.data as Blob;
  },
};
